// ── useFollowSeller.js ────────────────────────────────────────────────────────
// Handles follow/unfollow logic — syncs with Django backend
//
// ✅ FIX 1: Was using localStorage only — now calls backend API
// ✅ FIX 2: seller email was always '' because Django serializer exposes
//           seller_name (username) but NOT seller_email.
//           For OTP-based auth, the username IS the email/phone identifier,
//           so we fall back to product.seller (username) when email is empty.
// ✅ FIX 3: Added isFollowLoading to prevent double-clicks

import { useState, useEffect, useCallback } from 'react';
import useAuthentication from './useAuthentication';
import apiService from '../../../../services/api';

const useFollowSeller = ({ product, onOpenLoginModal }) => {
  const { checkAuthentication } = useAuthentication();

  const [isFollowing,     setIsFollowing]    = useState(false);
  const [followCount,     setFollowCount]    = useState(0);
  const [isFollowLoading, setIsFollowLoading] = useState(false);

  // ── Best identifier for the follow API ───────────────────────────────────
  // Django's toggle_follow / get_follow_status views look up by email.
  // api.js transformAdData maps:
  //   email: ad.seller_email || ad.contact_email || ''
  //   seller: ad.seller_name || ad.user_name  (this IS the email for OTP auth)
  //
  // So if product.email is empty, fall back to product.seller (username/email).
  const sellerIdentifier =
    product?.email        ||   // populated when backend returns seller_email
    product?.seller_email ||   // direct field check
    product?.seller       ||   // seller_name — equals the email in OTP auth
    product?.sellerName   ||
    '';

  // ── Fetch real follow status + count from backend on mount / product change
  useEffect(() => {
    if (!product?.id) return;

    if (!sellerIdentifier) {
      console.warn(
        '⚠️ useFollowSeller: no seller identifier — follow status unavailable.\n',
        'product.email:',       product?.email,       '\n',
        'product.seller_email:', product?.seller_email, '\n',
        'product.seller:',      product?.seller,
      );
      return;
    }

    let cancelled = false;

    const fetchFollowStatus = async () => {
      try {
        console.log(`🔍 Fetching follow status for: "${sellerIdentifier}"`);
        const res = await apiService.getFollowStatus(sellerIdentifier);
        if (cancelled) return;
        console.log('✅ Follow status:', res);
        setIsFollowing(res.is_following   || false);
        setFollowCount(res.follower_count || 0);
      } catch (err) {
        if (!cancelled) console.error('❌ Failed to fetch follow status:', err);
      }
    };

    fetchFollowStatus();
    return () => { cancelled = true; };
  }, [product?.id, sellerIdentifier]);

  // ── checkIfFollowing — kept for any callers in useProductModal ────────────
  const checkIfFollowing = useCallback(() => {
    // No-op: status is loaded from backend in the useEffect above.
  }, []);

  // ── Toggle follow/unfollow with optimistic UI update ─────────────────────
  const handleFollowToggle = useCallback(async () => {
    const auth = checkAuthentication();
    if (!auth.isAuthenticated) {
      if (onOpenLoginModal) onOpenLoginModal();
      else alert('Please log in to follow this seller');
      return;
    }

    if (!sellerIdentifier) {
      alert('Cannot follow this seller — seller information is unavailable');
      return;
    }

    if (isFollowLoading) return; // prevent double-clicks during pending request

    // ── Optimistic update — instant UI feedback ───────────────────────────
    const prevFollowing = isFollowing;
    const prevCount     = followCount;

    setIsFollowLoading(true);
    setIsFollowing(!prevFollowing);
    setFollowCount(prevFollowing ? Math.max(0, prevCount - 1) : prevCount + 1);

    try {
      console.log(`🎯 Toggling follow for: "${sellerIdentifier}"`);
      const res = await apiService.toggleFollow(sellerIdentifier);
      console.log('✅ Toggle follow response:', res);

      if (res.success) {
        // Sync to confirmed backend values
        setIsFollowing(res.is_following);
        setFollowCount(res.follower_count);
      } else {
        // Revert optimistic update on failure
        console.error('❌ Follow toggle failed:', res.error);
        setIsFollowing(prevFollowing);
        setFollowCount(prevCount);
        alert(res.error || 'Failed to update follow status. Please try again.');
      }
    } catch (err) {
      // Revert on network error
      console.error('❌ Follow toggle network error:', err);
      setIsFollowing(prevFollowing);
      setFollowCount(prevCount);
    } finally {
      setIsFollowLoading(false);
    }
  }, [
    checkAuthentication,
    onOpenLoginModal,
    sellerIdentifier,
    isFollowing,
    followCount,
    isFollowLoading,
  ]);

  return {
    isFollowing,
    followCount,
    isFollowLoading,
    setFollowCount,
    checkIfFollowing,
    handleFollowToggle,
  };
};

export default useFollowSeller;